import {STATUS} from '@jobseeker/common-core/constants';
import axiosInterceptorInstance from '@jobseeker/common-core/helpers/axiosInterceptor';

import type {
  CategoryJobFeedRequest,
  SimilarJobFeedRequest,
  TagJobFeedRequest,
  LocationJobFeedRequest,
  RecruiterJobFeedRequest,
  CityJobFeedRequest,
} from '@jobseeker/common-core/reducers/job/thunkServices/requestInterfaces';
import {AxiosResponse} from 'axios';

const axios = axiosInterceptorInstance(
  `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_JOB_SERVICE}`
);

const getCategoryJobs = async ({
  userId = '-1',
  catId,
  ...params
}: CategoryJobFeedRequest): Promise<unknown> => {
  try {
    const modifiedParams = {...params, categoryId: catId || params.catOrTagId};
    if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
      modifiedParams.size = 20;
    }
    const response = await axios.get(`/category/`, {
      params: modifiedParams,
      headers: {
        version: '2',
      },
    });
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

const getCategoryJobsForSitemap = async ({
  catId,
  ...params
}: CategoryJobFeedRequest): Promise<unknown> => {
  try {
    const response = await axios.get(`/sitemap/category/`, {
      params: {...params, categoryId: catId},
      headers: {
        version: '2',
      },
    });
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

const getNearByCityJobs = async ({
  userId = '-1',
  catId,
  ...params
}: CityJobFeedRequest): Promise<any> => {
  try {
    const response = await axios.get(`/nearby-cities-jobs/`, {
      params: {...params, ...(catId ? {categoryId: catId} : {})},
    });
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

const getLatestBlogs = async (category: any) => {
  try {
    const response = await axios.get(`/getLatestBlogs?category=${category}`);
    return response?.data?.data;
  } catch (error: unknown) {
    throw error;
  }
};

const getRecuiterJobFeed = async ({
  userId = '-1',
  recruiterId,
  ...params
}: RecruiterJobFeedRequest): Promise<unknown> => {
  try {
    const modifiedParams = {...params, recruiterId: recruiterId};
    // if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
    //   modifiedParams.size = 20;
    // }
    const response = await axios.get(`/recruiterfeed/`, {
      params: modifiedParams,
    });
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

const getTagJobs = async ({
  userId = '-1',
  ...params
}: TagJobFeedRequest): Promise<unknown> => {
  try {
    const modifiedParams = {
      ...params,
      keywordId: params.query || params.catOrTagId,
    };
    if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
      modifiedParams.size = 20;
    }
    const response = await axios.get(`/keyword/`, {
      params: modifiedParams,
      headers: {
        version: '2',
      },
    });
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

const getSimilarJobs = async ({
  userId = '-1',
  tags,
  ...params
}: SimilarJobFeedRequest): Promise<AxiosResponse<any, any>> => {
  try {
    const modifiedParams = {...params, tags: tags?.join(',')};
    // if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
    //   modifiedParams.size = 20;
    // }
    const response = await axios.get(`/similarjobs`, {
      params: modifiedParams,
      ...(params?.whatFor !== 'course' &&
        params?.whatFor !== 'hirist' && {
          headers: {
            version: '2',
          },
        }),
    });
    return response.data;
  } catch (error: unknown) {
    throw error;
  }
};

const tagIdBySlug = async (slug: string) => {
  try {
    const response: any = await axios.get(`/taginfo?slug=${slug}`, {});
    if (response[STATUS] === 200) {
      return {
        id: response['data']['data'][0].id,
        title: response['data']['data'][0].tagName,
        catId: response['data']['data'][0]?.categories[0] || -1,
      };
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
  return;
};

const tagSlugById = async (tagIds: string) => {
  try {
    const response: any = await axios.get(`/tagsById`, {
      params: {tagIds},
    });

    if (response.status === 200) {
      const data = response.data;

      return {
        data: data,
      };
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }

  return;
};

const tagIdsBySlug = async (slug: string) => {
  try {
    const response: any = await axios.get(`/tagIdsByName?slug=${slug}`);

    if (response.status === 200) {
      const data = response.data;

      return {
        data: data,
      };
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }

  return;
};

const getSavedJobs = async (page?: number) => {
  try {
    let url = `/saved-jobs?page=${page ?? 0}`;
    if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
      url += '&size=20';
    }
    const response = await axios.get(url, {});

    if (response[STATUS] === 200) {
      return response.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getRecruiterData = async (recruiterId?: number) => {
  try {
    const response = await axios.get(`/data/recruiter`, {
      params: {
        id: recruiterId,
      },
    });

    if (response[STATUS] === 200) {
      return response.data.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getCompanySimilarJobs = async (page?: number) => {
  try {
    let url = `/similar-company-jobs?page=${page ?? 0}`;
    // if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
    //   url += '&size=20';
    // }
    const response = await axios.get(url, {});
    if (response[STATUS] === 200) {
      return response.data.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getCompanyJobs = async (
  slug: string,
  page?: number,
  companyId?: number
) => {
  try {
    let url = companyId
      ? `/top-jobs?page=${page ?? 0}&slug=${slug}&companyId=${companyId || ''}`
      : `/top-jobs?page=${page ?? 0}&slug=${slug}`;

    // if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
    //   url += '&size=20';
    // }

    const response = await axios.get(url, {});
    if (response[STATUS] === 200) {
      return response.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getCompanyList = async (page?: number, params?: object) => {
  try {
    const response = await axios.get(`/company-list?page=${page ?? 0}`, {
      params: {...params},
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getCompanySearchList = async (page?: number, params?: object) => {
  try {
    const response = await axios.get(`/company-list/search?page=${page ?? 0}`, {
      params: {...params},
    });
    if (response[STATUS] === 200) {
      return response.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getAllCompaniesList = async () => {
  try {
    const response = await axios.get('/companies/slugs');
    if (response[STATUS] === 200) {
      return response.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getLocationSpecificJobs = async (
  info: LocationJobFeedRequest
): Promise<unknown> => {
  try {
    if (info.type === 'category') {
      delete info['type'];
      return await getCategoryJobs({
        catId: info.id,
        ...info,
      });
    }
    if (info.type == 'company') {
      delete info['type'];
      const data = await getCompanyJobs(info.id, info?.page ?? 0, 1);
      return data?.data;
    }
    delete info['type'];
    return await getTagJobs({
      query: info.id,
      ...info,
    });
  } catch (error: unknown) {
    throw error;
  }
};

const getSavedJobCount = async () => {
  try {
    const response = await axios.get(`/saved-jobs/count/`);
    return response.data;
  } catch (error) {
    throw error;
  }
};

const getAllTagsWithCategory = async () => {
  try {
    const response = await axios.get('/tags');
    if (response[STATUS] === 200) {
      return response['data'];
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};
const getAppliedJobs = async (page?: number, status?: string) => {
  try {
    let url = `/applied-jobs?page=${page ?? 0}&status=${status}`;
    // if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
    //   url += '&size=20';
    // }
    const response = await axios.get(url);
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

const getRecommendedJobs = async (page?: number) => {
  try {
    let url = `/recommended-jobs?page=${page ?? 0}`;
    // if (process.env.NEXT_PUBLIC_APP_NAME === 'hirist') {
    //   url += '&size=20';
    // }
    const response = await axios.get(url, {});

    if (response[STATUS] === 200) {
      return response.data;
    }
  } catch (error: unknown) {
    throw error;
  }
};

const getEarlyApplicantJobfeed = async (queryParams: string) => {
  try {
    const response = await axios.get(
      `/v2/early-applicant-jobfeed${queryParams}`
    );
    if (response[STATUS] === 200) {
      return response;
    }
  } catch (error: any) {
    return {
      error: {
        message: error.response?.data.error.message,
        code: error.response?.data.error.statusCode,
      },
    };
  }
};

const getFemaleJobs = async (
  femaleFilter?: number,
  minExp?: number,
  maxExp?: number,
  categoryIds?: string,
  joblocationIds?: string,
  start?: number,
  end?: number,
  functionalArea?: string,
  antifunctionalarea?: string,
  centralisedCompanyIds?: string,
  antiCentralisedCompanyIds?: string
) => {
  try {
    const params = new URLSearchParams();

    if (femaleFilter !== undefined)
      params.append('femaleFilter', String(femaleFilter));
    if (minExp !== undefined) params.append('minExp', String(minExp));
    if (maxExp !== undefined) params.append('maxExp', String(maxExp));
    if (joblocationIds) params.append('joblocationIds', joblocationIds);
    if (start !== undefined) params.append('start', String(start));
    if (end !== undefined) params.append('end', String(end));
    if (categoryIds) params.append('categoryIds', categoryIds);
    if (functionalArea !== undefined && functionalArea !== '0')
      params.append('functionalArea', functionalArea);
    if (antifunctionalarea !== undefined)
      params.append('antifunctionalarea', antifunctionalarea);
    if (centralisedCompanyIds !== undefined)
      params.append('centralisedCompanyIds', centralisedCompanyIds);
    if (antiCentralisedCompanyIds !== undefined)
      params.append('antiCentralisedCompanyIds', antiCentralisedCompanyIds);

    // Special case: Hirist app → add size
    // if (process.env.NEXT_PUBLIC_APP_NAME === "hirist") {
    //   params.append("size", "20");
    // }

    const url = `/female-jobs?${params.toString()}`;

    const response = await axios.get(url);

    if (response.status === 200) {
      console.log('ayush', response.data);
      return response.data;
    }
  } catch (error) {
    throw error;
  }
};

const getFreeTextSearchParams = async (query: string) => {
  try {
    const apiResponse = await axios.post(
      `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_JOB_SERVICE}/v2/extract-param`,
      {
        query,
      }
    );
    const responseData = apiResponse?.data;
    return responseData;
  } catch (error: unknown) {
    throw error;
  }
};

const getFeatureFlags = async () => {
  try {
    const timestamp = new Date().getTime();
    const apiResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_STATIC_IIMJOBS}/static/features/feature-flag.json?moment=${timestamp}`
    );
    const responseData = apiResponse?.data;
    return responseData;
  } catch (error) {
    throw error;
  }
};

const jobfeedService = {
  getCategoryJobs,
  getTagJobs,
  getSavedJobs,
  getCompanyJobs,
  getCompanySimilarJobs,
  getSavedJobCount,
  getCompanySearchList,
  getAppliedJobs,
  getSimilarJobs,
  tagIdBySlug,
  getLocationSpecificJobs,
  getRecuiterJobFeed,
  getRecruiterData,
  getAllTagsWithCategory,
  getLatestBlogs,
  getFemaleJobs,
  getRecommendedJobs,
  getEarlyApplicantJobfeed,
  getCompanyList,
  tagSlugById,
  getAllCompaniesList,
  getNearByCityJobs,
  getCategoryJobsForSitemap,
  tagIdsBySlug,
  getFreeTextSearchParams,
  getFeatureFlags,
};

export default jobfeedService;
